import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiCoffee, FiShoppingBag, FiStar, FiMapPin, 
  FiCompass, FiCamera, FiMusic, FiHeart, 
  FiArrowRight, FiActivity, FiX, FiCheck
} = FiIcons;

const FAVORITES_KEY = "nearbyFavorites_v1.1";

const Nearby = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [favorites, setFavorites] = useState([]);
  
  // Default map shows property location
  const propertyAddress = "7601 Canada Ave, Orlando, FL 32819";
  const defaultMap = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3507.412462391093!2d-81.4709216!3d28.4552097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e77e8a3a3a3a3b%3A0x6b9a9a9a9a9a9a9a!2s7601%20Canada%20Ave%2C%20Orlando%2C%20FL%2032819!5e0!3m2!1sen!2sus!4v1710345678901!5m2!1sen!2us`;
  
  const [mapSrc, setMapSrc] = useState(defaultMap);
  const [externalLink, setExternalLink] = useState(`https://maps.google.com/?q=${encodeURIComponent(propertyAddress)}`);

  useEffect(() => {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (raw) {
      try {
        setFavorites(JSON.parse(raw));
      } catch (e) {
        console.error("Failed to parse favorites", e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (location) => {
    setFavorites(prev => {
      const exists = prev.find(f => f.name === location.name);
      if (exists) {
        return prev.filter(f => f.name !== location.name);
      }
      return [...prev, location];
    });
  };

  const isFavorite = (name) => favorites.some(f => f.name === name);

  // Update map when category changes
  useEffect(() => {
    if (selectedCategory === 'all') {
      setMapSrc(defaultMap);
      setExternalLink(`https://maps.google.com/?q=${encodeURIComponent(propertyAddress)}`);
    } else {
      const catQuery = selectedCategory === 'clubs' ? 'nightclubs+lounges' : selectedCategory;
      setMapSrc(`https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d3507.412462391093!2d-81.4709216!3d28.4552097!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1s${catQuery}!5e0!3m2!1sen!2sus!4v1710345678901!5m2!1sen!2us`);
      setExternalLink(`https://www.google.com/maps/search/?api=1&query=${catQuery}+near+${encodeURIComponent(propertyAddress)}`);
    }
  }, [selectedCategory]);

  const allLocations = [
    {
      name: "Hash House A Go Go",
      category: "eating",
      address: "5350 International Dr, Orlando, FL 32819",
      description: "Famous large portion brunch spot serving 'twisted farm food' and giant pancakes.",
      distance: "3 min drive",
      icon: FiCoffee
    },
    {
      name: "Amor em Pedaços Bakery",
      category: "eating",
      address: "6432 International Dr, Orlando, FL 32819",
      description: "Authentic Brazilian bakery known for cakes, sweets, and pão de queijo.",
      distance: "5 min drive",
      icon: FiCoffee
    },
    {
      name: "Cafe Mineiro Brazilian Steakhouse",
      category: "eating",
      address: "6432 International Dr, Orlando, FL 32819",
      description: "Authentic Brazilian steakhouse with buffet and rodizio near International Drive.",
      distance: "5 min drive",
      icon: FiCoffee
    },
    {
      name: "Yard House",
      category: "eating",
      address: "8361 International Dr, Orlando, FL 32819",
      description: "Sports bar with 100+ beers on tap and extensive menu, great for casual business meetings.",
      distance: "5 min drive",
      icon: FiCoffee
    },
    {
      name: "P.F. Chang's",
      category: "eating",
      address: "9101 International Dr Suite 100, Orlando, FL 32819",
      description: "Asian fusion cuisine in a sophisticated setting.",
      distance: "7 min drive",
      icon: FiCoffee
    },
    {
      name: "Orlando International Premium Outlets",
      category: "shopping",
      address: "4951 International Dr, Orlando, FL 32819",
      description: "Discount designer outlet mall featuring 180+ high-end brands.",
      distance: "12 min drive",
      icon: FiShoppingBag
    },
    {
      name: "The Mall at Millenia",
      category: "shopping",
      address: "4200 Conroy Rd, Orlando, FL 32839",
      description: "Upscale shopping experience with luxury brands and elegant architecture.",
      distance: "15 min drive",
      icon: FiShoppingBag
    },
    {
      name: "The Florida Mall",
      category: "shopping",
      address: "8001 S Orange Blossom Trl, Orlando, FL 32809",
      description: "Large all-in-one shopping center with 250+ stores.",
      distance: "20 min drive",
      icon: FiShoppingBag
    },
    {
      name: "Walt Disney World Resort",
      category: "attractions",
      address: "Walt Disney World Resort, Orlando, FL",
      description: "The world's most magical destination with four theme parks and two water parks.",
      distance: "15–20 min drive",
      icon: FiStar
    },
    {
      name: "Universal Orlando Resort",
      category: "attractions",
      address: "6000 Universal Blvd, Orlando, FL 32819",
      description: "Epic adventure awaits with Universal Studios and Islands of Adventure.",
      distance: "7 min drive",
      icon: FiStar
    },
    {
      name: "SeaWorld Orlando",
      category: "attractions",
      address: "7007 SeaWorld Dr, Orlando, FL 32821",
      description: "Marine life adventure park with thrilling rides and animal encounters.",
      distance: "10 min drive",
      icon: FiHeart
    },
    {
      name: "ICON Park & The Wheel",
      category: "attractions",
      address: "8375 International Dr, Orlando, FL 32819",
      description: "400-foot observation wheel with breathtaking views of the city.",
      distance: "5 min drive",
      icon: FiCompass
    },
    {
      name: "Mango's Tropical Cafe",
      category: "clubs",
      address: "8126 International Dr, Orlando, FL 32819",
      description: "World-class nightclub and dinner show featuring high-energy Latin dancing and live music.",
      distance: "5 min drive",
      icon: FiMusic
    },
    {
      name: "ICEBAR Orlando",
      category: "clubs",
      address: "8967 International Dr, Orlando, FL 32819",
      description: "The world's largest permanent ice bar featuring a frozen lounge and a warm Fire Lounge for dancing.",
      distance: "8 min drive",
      icon: FiActivity
    },
    {
      name: "Blue Martini Lounge",
      category: "clubs",
      address: "4200 Conroy Rd #246, Orlando, FL 32839",
      description: "Upscale lounge at Mall at Millenia known for signature martinis, live music, and late-night dancing.",
      distance: "15 min drive",
      icon: FiMusic
    }
  ];

  const categories = [
    { id: 'eating', title: "Eat", icon: FiCoffee },
    { id: 'shopping', title: "Shop", icon: FiShoppingBag },
    { id: 'attractions', title: "Fun", icon: FiCamera },
    { id: 'clubs', title: "Clubs", icon: FiMusic }
  ];

  return (
    <div className="min-h-screen bg-[#fcfcfc] pt-32 pb-24">
      <section className="px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-col"
            >
              <span className="text-accent-orange font-black tracking-[0.3em] uppercase text-[10px] mb-6 block underline underline-offset-8 decoration-2">Local Guide</span>
              <h1 className="text-5xl sm:text-7xl lg:text-9xl font-serif font-black text-black uppercase tracking-tighter leading-[0.85] mb-8">
                Explore <br />Orlando
              </h1>
              <p className="text-black/50 font-bold uppercase tracking-widest text-xs leading-loose max-w-md">
                All recommendations are within a 15-minute radius of 7601 Canada Avenue. Curated for your convenience.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative h-[350px] sm:h-[450px] lg:h-[600px] bg-white rounded-[40px] shadow-2xl border border-black/5 overflow-hidden group"
            >
              <a 
                href={externalLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full h-full relative"
              >
                <iframe
                  src={mapSrc}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  className="grayscale group-hover:grayscale-0 transition-all duration-700 pointer-events-none"
                ></iframe>
                
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                  <div className="bg-black text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all shadow-2xl flex items-center gap-2">
                    Open in Google Maps <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
                  </div>
                </div>
              </a>
            </motion.div>
          </div>

          <AnimatePresence>
            {favorites.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-12 bg-black text-white rounded-[40px] p-8 sm:p-12 overflow-hidden shadow-2xl border border-white/10"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
                  <div>
                    <h2 className="text-2xl font-serif font-black uppercase tracking-tight">Your Trip Plan</h2>
                    <p className="text-white/50 text-[10px] font-black uppercase tracking-widest mt-2">{favorites.length} Locations Saved</p>
                  </div>
                  <button 
                    onClick={() => setFavorites([])}
                    className="text-[10px] font-black uppercase tracking-widest border border-white/20 px-6 py-3 rounded-full hover:bg-white/10 transition-all"
                  >
                    Clear All
                  </button>
                </div>
                
                <div className="flex flex-wrap gap-4">
                  {favorites.map((fav) => (
                    <motion.div 
                      layout
                      key={fav.name}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4 group"
                    >
                      <div className="w-10 h-10 bg-accent-orange rounded-xl flex items-center justify-center">
                        <SafeIcon icon={fav.icon} className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-sm font-bold uppercase tracking-tight">{fav.name}</h3>
                        <p className="text-[10px] text-white/40 uppercase tracking-widest">{fav.distance}</p>
                      </div>
                      <button 
                        onClick={() => toggleFavorite(fav)}
                        className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-red-500 transition-colors"
                      >
                        <SafeIcon icon={FiX} className="w-4 h-4" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full py-12 border-y border-black/5"
          >
            <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-8 py-4 rounded-full text-[10px] font-black uppercase tracking-widest transition-all duration-300 ${
                  selectedCategory === 'all'
                    ? 'bg-black text-white shadow-xl scale-105'
                    : 'bg-black/5 text-black/60 hover:bg-black/10'
                }`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-8 py-4 rounded-full text-[10px] font-black uppercase tracking-widest transition-all duration-300 flex items-center gap-3 ${
                    selectedCategory === cat.id
                      ? 'bg-black text-white shadow-xl scale-105'
                      : 'bg-black/5 text-black/60 hover:bg-black/10'
                  }`}
                >
                  <SafeIcon icon={cat.icon} className="w-4 h-4" />
                  {cat.title}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20">
        {categories.filter(c => selectedCategory === 'all' || c.id === selectedCategory).map(cat => (
          <div key={cat.id}>
            <div className="flex items-center gap-4 mb-10">
              <h2 className="text-3xl font-serif font-black uppercase tracking-tight text-black">{cat.title}</h2>
              <div className="flex-1 h-px bg-black/10" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {allLocations
                .filter(loc => loc.category === cat.id)
                .map((item) => (
                  <div key={item.name} className="relative group">
                    <motion.a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="block bg-white p-8 rounded-[32px] shadow-sm border border-black/5 hover:border-accent-orange/30 hover:shadow-xl transition-all duration-500 h-full flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-accent-orange mb-4">
                          <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                          {item.distance}
                        </div>
                        <h3 className="text-2xl font-serif font-black text-black uppercase tracking-tight mb-3 group-hover:text-accent-orange transition-colors">
                          {item.name}
                        </h3>
                        <p className="text-sm text-black/60 leading-relaxed mb-6">
                          {item.description}
                        </p>
                      </div>
                      <div className="flex justify-end">
                        <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-black/20 group-hover:text-accent-orange transition-colors" />
                      </div>
                    </motion.a>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFavorite(item);
                      }}
                      className={`absolute top-6 right-6 w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-md ${
                        isFavorite(item.name)
                          ? 'bg-accent-orange text-white'
                          : 'bg-white text-black/20 hover:text-accent-orange hover:bg-black/5'
                      }`}
                    >
                      <SafeIcon icon={isFavorite(item.name) ? FiCheck : FiHeart} className="w-5 h-5" />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Nearby;